/**
 * Session/local storage persistence + MRT<->DataGrid model translation for the
 * table wrapper. Pure and side-effect-free apart from the storage reads/writes,
 * so the translation helpers can be unit-tested directly.
 *
 * Storage shape is persisted in DataGrid terms: { page, pageSize, quickFilter }.
 */

export const STORAGE_PREFIX = 'tbl_';
export const PREFERENCE_DEFAULTS_KEY = 'tablePreferenceDefaults';

export function getPreferenceDefaults() {
  try {
    const stored = localStorage.getItem(PREFERENCE_DEFAULTS_KEY);
    return stored ? JSON.parse(stored) : { rowsPerPage: 15, pageIndex: 0 };
  } catch {
    return { rowsPerPage: 15, pageIndex: 0 };
  }
}

export function setPreferredPageSize(pageSize) {
  try {
    const stored = localStorage.getItem(PREFERENCE_DEFAULTS_KEY);
    const prefs = stored ? JSON.parse(stored) : {};
    localStorage.setItem(
      PREFERENCE_DEFAULTS_KEY,
      JSON.stringify({ ...prefs, rowsPerPage: pageSize })
    );
  } catch {
    // ignore quota/availability errors
  }
}

export function getTableStateFor(tableKey) {
  if (!tableKey) return undefined;
  try {
    const stored = sessionStorage.getItem(STORAGE_PREFIX + tableKey);
    return stored ? JSON.parse(stored) : undefined;
  } catch {
    return undefined;
  }
}

export function saveTableState(tableKey, state) {
  if (!tableKey) return;
  try {
    sessionStorage.setItem(STORAGE_PREFIX + tableKey, JSON.stringify(state));
  } catch {
    // sessionStorage full or unavailable
  }
}

/**
 * globalFilter (single "contains" phrase, MRT semantics) <-> DataGrid
 * quickFilterValues. We keep the whole phrase as ONE value so matching stays a
 * single substring test rather than DataGrid's default space-split AND.
 */
export function globalFilterToQuickFilterValues(globalFilter) {
  const trimmed = (globalFilter ?? '').trim();
  return trimmed.length ? [trimmed] : [];
}

export function quickFilterValuesToGlobalFilter(values) {
  if (!Array.isArray(values) || values.length === 0) return '';
  // Rejoin defensively in case something upstream split the phrase.
  return values.join(' ');
}

/** Build the initial pagination model from props + persisted/default state. */
export function resolveInitialPagination({
  savedState,
  defaults,
  propPage,
  propPageSize,
  isManualPagination
}) {
  const pageSize = (() => {
    if (isManualPagination && propPageSize !== undefined) return propPageSize;
    return savedState?.pageSize ?? defaults.rowsPerPage ?? propPageSize ?? 15;
  })();
  const page = (() => {
    if (propPage !== undefined) return propPage;
    return savedState?.page ?? defaults.pageIndex ?? 0;
  })();
  return { page, pageSize };
}
